const KNOWN_FIELD_ALIASES = {
  email: ["subscriber", "email", "email address", "email_address", "e-mail"],
  sent: ["sent", "emails sent", "total sent"],
  opens: ["opens", "opened", "total opens", "open count", "open_count"],
  clicks: ["clicks", "clicked", "total clicks", "click count", "click_count"],
  subscribedAt: ["subscribed", "created", "signup date", "subscribed at", "date subscribed", "date", "created_at", "signup_date"],
  country: ["country", "location country"],
  firstName: ["name", "first name", "firstname", "first_name"],
  lastName: ["last name", "lastname", "last_name", "surname"],
  notes: ["notes", "note", "comment"],
  groups: ["groups", "group"],
  segments: ["segments", "segment"],
  tags: ["tags", "tag"],
  status: ["status", "subscriber status", "state"],
};

const normalizeHeader = (value) => String(value || "").trim().toLowerCase().replace(/[_\s]+/g, " ");
const normalizeValue = (value) => value instanceof Date ? value.toISOString() : String(value ?? "").trim();
export const EMAIL_REGEX = /^[^\s<>@,;]+@[^\s<>@,;]+\.[^\s<>@,;]+$/;

export const parseCSV = (line) => {
  const rows = [];
  let cols = [];
  let current = "";
  let inQuotes = false;
  let closed = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i += 1; }
      else if (inQuotes) { inQuotes = false; closed = true; }
      else if (current === '' && !closed) { inQuotes = true; }
      else { throw new Error('CSV contains an unexpected quote.'); }
    } else if (char === "," && !inQuotes) {
      cols.push(current.trim());
      current = "";
      closed = false;
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      cols.push(current.trim());
      if (cols.some(Boolean)) rows.push(cols);
      cols = []; current = ''; closed = false;
      if (char === '\r' && line[i + 1] === '\n') i++;
    } else {
      if (closed && char.trim()) throw new Error('CSV contains text after a closing quote.');
      current += char;
    }
  }
  if (inQuotes) throw new Error('CSV has an unclosed quoted field.');
  cols.push(current.trim());
  if (cols.some(Boolean)) rows.push(cols);
  return rows;
};

const toNumber = (value) => {
  const n = Number(String(value ?? "").replace(/,/g, "").trim());
  return Number.isSafeInteger(n) && n >= 0 ? n : null;
};

const parseDateWithRaw = (value) => {
  const raw = normalizeValue(value);
  if (!raw) return { subscribedAt: null, subscribedAtRaw: "" };
  // BookFunnel exports an English timestamp with a US time-zone abbreviation.
  // Parse it explicitly so Safari and desktop browsers produce the same date.
  const bookFunnel = raw.match(/^(?:Sun|Mon|Tue|Wed|Thu|Fri|Sat) (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{1,2}) (\d{2}):(\d{2}):(\d{2}) (UTC|GMT|EST|EDT|CST|CDT|MST|MDT|PST|PDT) (\d{4})$/);
  let parsed;
  if (bookFunnel) {
    const [, month, day, hour, minute, second, zone, year] = bookFunnel;
    const offset = { UTC: 0, GMT: 0, EST: -5, EDT: -4, CST: -6, CDT: -5, MST: -7, MDT: -6, PST: -8, PDT: -7 }[zone];
    const local = new Date(Date.UTC(+year, 'Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec'.split(' ').indexOf(month), +day, +hour, +minute, +second));
    if (local.getUTCDate() !== +day || +hour > 23 || +minute > 59 || +second > 59) return { subscribedAt: null, subscribedAtRaw: raw };
    parsed = new Date(local.getTime() - offset * 3600000);
  } else parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return { subscribedAt: null, subscribedAtRaw: raw };
  return { subscribedAt: parsed.toISOString(), subscribedAtRaw: raw };
};

const mapStatus = (value) => {
  const v = normalizeValue(value).toLowerCase();
  if (["unsubscribed", "unsubscribe"].includes(v)) return "Unsubscribed";
  if (["bounced"].includes(v)) return "Bounced";
  if (["complained", "spam"].includes(v)) return "Complained";
  if (["inactive"].includes(v)) return "Inactive";
  if (["pending", "unconfirmed"].includes(v)) return "Pending";
  if (["active", "subscribed"].includes(v)) return "Subscribed";
  return v ? null : "Subscribed";
};

const splitList = (value) => normalizeValue(value).split(/[;,]/).map((v) => v.trim()).filter(Boolean);

export const parseImportFile = async (file) => {
  if (!file) throw new Error('Choose a CSV or Excel file first.');
  const extension = file.name.toLowerCase().split(".").pop();
  if (!['csv', 'xlsx', 'xls'].includes(extension)) throw new Error('Choose a CSV (.csv) or Excel (.xlsx or .xls) file.');
  if (file.size > 5 * 1024 * 1024) throw new Error('Choose a file smaller than 5 MB.');
  let rows, sheetName = null, rowOffset = 0;
  const formulaRows = new Set();
  if (extension === 'csv') {
    const text = (await file.text()).replace(/^\uFEFF/, '');
    rows = parseCSV(text);
  } else {
    const XLSX = await import('xlsx');
    let workbook;
    try {
      const data = new Uint8Array(await file.arrayBuffer());
      const signature = Array.from(data.slice(0, 4)).join(',');
      if (extension === 'xlsx' ? signature !== '80,75,3,4' : signature !== '208,207,17,224') throw new Error('Invalid workbook format');
      workbook = XLSX.read(data, { type: 'array', cellDates: true, sheetRows: 5002, cellHTML: false, cellStyles: false });
    } catch { throw new Error('Could not read this Excel file. Save it as an unencrypted .xlsx, .xls, or CSV file and try again.'); }
    sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    if (!sheet?.['!ref']) throw new Error('The first worksheet is empty. Put your subscriber list on the first worksheet.');
    const range = XLSX.utils.decode_range(sheet['!fullref'] || sheet['!ref']);
    if (range.e.r - range.s.r > 5000) throw new Error('Import up to 5,000 rows per file.');
    if (range.e.c - range.s.c > 100) throw new Error('The worksheet has too many columns. Keep only your subscriber list.');
    rowOffset = range.s.r;
    rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: '', blankrows: true });
    for (let r = range.s.r; r <= range.e.r; r++) for (let c = range.s.c; c <= range.e.c; c++) {
      if (sheet[XLSX.utils.encode_cell({ r, c })]?.f) formulaRows.add(r + 1);
    }
    rows = rows.map(row => row.map(normalizeValue));
    while (rows.length && !rows[0].some(Boolean)) { rows.shift(); rowOffset++; }
    while (rows.length && !rows[rows.length - 1].some(Boolean)) rows.pop();
    const width = rows[0]?.length || 0;
    rows = rows.map(row => [...row, ...Array(Math.max(0, width - row.length)).fill('')]);
  }
  if (rows.length < 2) throw new Error('Include a header and at least one subscriber row.');
  if (rows.length > 5001) throw new Error('Import up to 5,000 rows per file.');

  const headerCells = rows[0];
  if (headerCells.some(h => !h.trim() || ['__proto__', 'prototype', 'constructor'].includes(h.trim()) || h.length > 100)) throw new Error('The file has an empty, unsafe, or overly long column name.');
  if (new Set(headerCells.map(normalizeHeader)).size !== headerCells.length) throw new Error('The file has duplicate column names.');
  if (formulaRows.has(rowOffset + 1)) throw new Error('Use plain column names instead of Excel formulas.');
  const headerMap = headerCells.map((h, idx) => ({ original: h, normalized: normalizeHeader(h), idx }));
  const getColumnIndex = (aliases) => {
    const hit = headerMap.find((h) => aliases.map(normalizeHeader).includes(h.normalized));
    return hit ? hit.idx : -1;
  };

  const mapped = Object.fromEntries(Object.entries(KNOWN_FIELD_ALIASES).map(([key, aliases]) => [key, getColumnIndex(aliases)]));
  if (mapped.email === -1) throw new Error("Could not find an Email column.");
  const confirmed = getColumnIndex(['confirmed']), consented = getColumnIndex(['consented']);
  const bookFunnel = confirmed >= 0 && consented >= 0 && getColumnIndex(['claimed', 'downloaded', 'filetypes', 'devices']) >= 0;
  if (bookFunnel && mapped.subscribedAt < 0) mapped.subscribedAt = getColumnIndex(['claimed']);

  const consumed = new Set(Object.values(mapped).filter((i) => i >= 0));
  if (bookFunnel) consumed.delete(mapped.status);
  const customHeaderEntries = headerMap.filter((h) => !consumed.has(h.idx));
  if (customHeaderEntries.length > 50) throw new Error('Use up to 50 custom fields.');

  const seen = new Map();
  const parsed = [];
  const invalidRows = [];

  rows.slice(1).forEach((cols, i) => {
    const rowNumber = i + 2 + rowOffset;
    if (!cols.some(value => normalizeValue(value))) return;
    const email = normalizeValue(cols[mapped.email]).toLowerCase();
    if (formulaRows.has(rowNumber)) {
      invalidRows.push({ rowNumber, emailValue: email, reason: 'Replace Excel formulas with values before importing' }); return;
    }
    if (cols.length !== headerCells.length) {
      invalidRows.push({ rowNumber, emailValue: email, reason: 'Wrong number of columns' }); return;
    }
    if (!EMAIL_REGEX.test(email) || email.length > 254) {
      invalidRows.push({ rowNumber, emailValue: email, reason: "Invalid email format" });
      return;
    }
    if (seen.has(email)) {
      const previous = parsed.findIndex(s => s.email === email);
      if (previous >= 0) {
        parsed.splice(previous, 1);
        invalidRows.push({ rowNumber: seen.get(email), emailValue: email, reason: 'Duplicate email: all occurrences excluded; combine them into one row' });
      }
      invalidRows.push({ rowNumber, emailValue: email, reason: "Duplicate email: all occurrences excluded; combine them into one row" });
      return;
    }
    seen.set(email, rowNumber);
    const exportedStatus = normalizeValue(cols[mapped.status]);
    const status = mapStatus(bookFunnel && exportedStatus.toLowerCase() === 'saved to csv' ? '' : exportedStatus);
    if (!status) { invalidRows.push({ rowNumber, emailValue: email, reason: 'Unknown status; specify Subscribed, Unsubscribed, Inactive, Bounced, or Complained' }); return; }
    if (bookFunnel && status === 'Subscribed' && ![confirmed, consented].every(index => ['yes', 'true', '1'].includes(normalizeValue(cols[index]).toLowerCase()))) {
      invalidRows.push({ rowNumber, emailValue: email, reason: 'BookFunnel newsletter signup must be both Confirmed and Consented' }); return;
    }

    const sent = toNumber(cols[mapped.sent]);
    const opens = toNumber(cols[mapped.opens]);
    const clicks = toNumber(cols[mapped.clicks]);
    const { subscribedAt, subscribedAtRaw } = parseDateWithRaw(cols[mapped.subscribedAt]);
    if ([sent, opens, clicks].some(n => n == null) || (subscribedAtRaw && !subscribedAt)) {
      invalidRows.push({ rowNumber, emailValue: email, reason: 'Invalid statistics or subscription date' }); return;
    }

    const groups = splitList(cols[mapped.groups]);
    const segments = splitList(cols[mapped.segments]);
    const tags = splitList(cols[mapped.tags]);
    const notes = normalizeValue(cols[mapped.notes]);
    if ([mapped.firstName, mapped.lastName, mapped.country, mapped.subscribedAt].some(index => normalizeValue(cols[index]).length > 200) || notes.length > 2000 || [groups, segments, tags].some(values => values.length > 50 || values.some(value => value.length > 100)) || customHeaderEntries.some(h => normalizeValue(cols[h.idx]).length > 1000)) {
      invalidRows.push({ rowNumber, emailValue: email, reason: 'A field or list exceeds the subscriber import limits' }); return;
    }

    const customFields = Object.create(null);
    customHeaderEntries.forEach((h) => {
      const val = normalizeValue(cols[h.idx]);
      if (val) customFields[h.original] = val;
    });

    parsed.push({
      email,
      firstname: normalizeValue(cols[mapped.firstName]),
      lastname: normalizeValue(cols[mapped.lastName]),
      country: normalizeValue(cols[mapped.country]),
      notes,
      source: "import",
      importSource: extension,
      subscribedAt,
      subscribedAtRaw,
      status,
      stats: {
        sent,
        opens,
        clicks,
        openRate: sent > 0 ? opens / sent : 0,
        clickRate: sent > 0 ? clicks / sent : 0,
        ctor: opens > 0 ? clicks / opens : 0,
      },
      groups,
      segments,
      tags,
      customFields,
      opens,
      clicks,
    });
  });

  return { parsed, invalidRows, totalRows: rows.length - 1, format: bookFunnel ? 'BookFunnel' : extension === 'csv' ? 'CSV' : 'Excel', sheetName };
};

export const errorReport = rows => 'row,email,reason\r\n' + rows.map(r => [r.rowNumber, r.emailValue, r.reason].map(value => {
  let s = String(value ?? '');
  if (/^[=+@\-\t\r\n]/.test(s)) s = "'" + s;
  return '"' + s.replace(/"/g, '""') + '"';
}).join(',')).join('\r\n');

